import { ChangeEvent, useState, FocusEvent } from 'react';
import { Control, Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import {
  FormControl,
  FormHelperText,
  InputLabelProps,
  InputProps,
  OutlinedTextFieldProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { autoCorrectDate } from '../../helper';

export type ControlledTextFieldProps = TextFieldProps & {
  control: Control<any, any>;
  name: string;
  required?: boolean;
  errorMessage?: string;
  helperText?: string;
  InputLabelProps?: InputLabelProps;
  InputProps?: InputProps;
  shouldUnregister?: boolean;
  shrink?: boolean;
};

export const ControlledTextField = ({
  helperText,
  control,
  name,
  shrink,
  required,
  errorMessage,
  shouldUnregister,
  onChange,
  ...props
}: ControlledTextFieldProps) => {
  const { t} = useTranslation();
  const [hasFocus, setHasFocus] = useState(false);
  const isDate = props.type === 'date';

  return (
    <Controller
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      rules={{
        required: required ? t('common.mandatoryField') : false
      }}
      render={({ field: { onChange: onControlledChange, onBlur, value = '', ref }, fieldState: { error } }) => (
        <FormControl
          error={Boolean(error)}
          sx={{
            width: props.fullWidth ? '100%' : 'auto',
            '& label': {
              top: !hasFocus && !value ? '-8px' : 'initial',
            },
          }}
        >
          {isDate ? (
            <InputMask
              mask="99/99/9999"
              value={value || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                const corrected = autoCorrectDate({
                  input: newValue
                });
                onControlledChange(corrected);
                onChange?.({
                  ...e,
                  target: {
                    ...e.target,
                    value: corrected
                  }
                });
              }}
              onBlur={() => {
                onBlur();
                setHasFocus(false);
              }}
              onFocus={(event: FocusEvent<HTMLInputElement>) => {
                setHasFocus(true);
                props?.onFocus?.(event);
              }}
            >
              {(inputProps: any) => (
                <TextField
                  {...props}
                  {...inputProps}
                  variant="outlined"
                  size="small"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  error={Boolean(error)}
                  helperText={error?.message || ''}
                  InputLabelProps={{
                    shrink: Boolean(shrink),
                  }}
                  aria-describedby={`component-error-text-${name}`}
                />
              )}
            </InputMask>
          ) : (
            <TextField
              {...(props as Partial<OutlinedTextFieldProps>)}
              value={value}
              onChange={(e) => {
                onControlledChange(e.target.value);
                onChange?.(e);
              }}
              onBlur={() => {
                onBlur?.();
                setHasFocus(false);
              }}
              onFocus={(event) => {
                setHasFocus(true);
                props?.onFocus?.(event);
              }}
              inputRef={ref}
              variant="outlined"
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              aria-describedby={`component-error-text-${name}`}
            />
          )}
          {error && (error.message || errorMessage) && (
            <FormHelperText id={`component-error-text-${name}`}>
              {error.message || errorMessage}
            </FormHelperText>
          )}
          {helperText && !(error && (error.message || errorMessage)) && (
            <FormHelperText id={`component-error-text-${name}`}>
              {helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};
